import {
  IconCheckSquare,
  IconEnvelope,
  IconPhone,
  IconUserCircle,
} from '@maia/icons';
import {
  ChangedAssigneeDivider,
  ChangedAssigneeUnassignedDivider,
  ChangedPriorityDivider,
  ChangedStatusDivider,
  ChangedTeamDivider,
  NoteCard,
  PageHeader,
  RichTextEditor,
  Tag,
} from '@maia/ui';
import { fileToBase64, formatDate, getFullName } from '@maia/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback } from 'react';
import { match, P } from 'ts-pattern';
import {
  getTaskOptions,
  usePostMessage,
  useUpdateOptions,
} from '../../../features/task/task/queryOptions';

import { useGetFilterOptionData } from '../../../features/task/utils/get-filter-options';
import {
  FormOptionsData,
  TaskDetailsSidebar,
} from '../../../features/task/task/task-details-sidebar';
import { useScrollToBottom } from '../../../hooks/useScrollToBottom';
import { validateSearch } from '../../../utils/pagination';
import { IAttachmentEntity } from '@maia/shared';

export const Route = createFileRoute('/_authenticated/tasks/$guid')({
  component: Page,
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: ({ context: { queryClient }, params: { guid } }) =>
    queryClient.ensureQueryData(getTaskOptions(guid)),
  validateSearch: validateSearch,
});

function Page() {
  const data = Route.useLoaderData();

  const { brokers, teams } = useGetFilterOptionData();
  const { data: tasks } = useSuspenseQuery(getTaskOptions(data.guid, data));
  const scrollRef = useScrollToBottom([tasks]);

  const { mutate: sendMessage } = usePostMessage({
    customerGuid: data.customer?.guid ?? '',
    taskGuid: data.guid,
  });

  const { mutate: updateOptions } = useUpdateOptions({
    customerGuid: data.customer?.guid ?? '',
    taskGuid: data.guid,
  });

  const handleSendMessage = useCallback(
    async (message: string, attachments: Array<IAttachmentEntity>) => {
      const files = await Promise.all(
        attachments.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            filename: file.name,
            mimetype: file.type,
            encoded_document: base64,
          };
        }),
      );

      return sendMessage({ body: message, attachments: files });
    },
    [sendMessage],
  );

  const handleOptionsUpdate = useCallback(
    (params: FormOptionsData) => updateOptions(params),
    [updateOptions],
  );

  return (
    <div className="layout-sidebar">
      <PageHeader
        icon={IconCheckSquare}
        title={tasks.title}
        reference={`#${tasks.reference}`}
        status={<Tag {...tasks.status} />}
        specs={[
          ...(tasks.customer?.consumer.firstName ||
          tasks.customer?.consumer.lastName
            ? [
                {
                  icon: IconUserCircle,
                  text: getFullName(tasks.customer.consumer),
                },
              ]
            : []),

          ...(tasks.customer?.consumer.email
            ? [
                {
                  icon: IconEnvelope,
                  text: tasks.customer?.consumer.email ?? '',
                },
              ]
            : []),

          ...(tasks.customer?.consumer.phone
            ? [{ icon: IconPhone, text: tasks.customer?.consumer.phone ?? '' }]
            : []),
        ]}
        className="area-page-header"
        isSticky
      />
      <div
        ref={scrollRef}
        className="flex flex-col area-page-content-list relative content-grid overflow-y-auto h-[calc(100vh-var(--toolbar-height)-108px)]"
      >
        <div className="col-span-full flex-grow">
          <div className="flex flex-col overflow-y-auto gap-y-24">
            <NoteCard
              user={`${data.createdBy.firstName ?? data.createdBy.initials} ${data.createdBy.lastName}`}
              title=""
              date={formatDate(data.createdAt)}
              attachments={data.attachments}
            >
              {data.description}
            </NoteCard>
            {tasks.history.map((item, index) =>
              match(item)
                .with({ type: 'status' }, (activity) => (
                  <ChangedStatusDivider
                    key={`${activity.createdAt.toString()}${activity.status.label}`}
                    color={activity.status.variant}
                    status={activity.status.label}
                    date={formatDate(activity.createdAt)}
                  />
                ))
                .with({ type: 'priority' }, (priority) => (
                  <ChangedPriorityDivider
                    key={`${priority.createdAt.toString()}${priority.priority}`}
                    color={priority.priority.variant}
                    variant="square"
                    status={priority.priority.label}
                    date={formatDate(priority.createdAt)}
                  />
                ))
                .with({ type: 'assignee' }, (assignee) => {
                  if (!assignee.assignee) {
                    return (
                      <ChangedAssigneeUnassignedDivider
                        key={`${assignee.createdAt.toString()}`}
                        date={formatDate(assignee.createdAt)}
                      />
                    );
                  }
                  return (
                    <ChangedAssigneeDivider
                      key={`${assignee.createdAt.toString()}${assignee.assignee.guid}`}
                      initials={assignee.assignee.initials}
                      name={`${assignee.assignee.firstName ?? assignee.assignee.initials} ${assignee.assignee.lastName}`}
                      date={formatDate(assignee.createdAt)}
                    />
                  );
                })
                .with({ type: 'team' }, (team) => (
                  <ChangedTeamDivider
                    key={`${team.createdAt.toString()}${team.team}${index}`}
                    team={team.team}
                    date={formatDate(team.createdAt)}
                  />
                ))
                .with({ type: 'note' }, (note) => {
                  return (
                    <NoteCard
                      key={`${note.createdAt.toString()}-${note.content}`}
                      user={`${note.createdBy.firstName ?? note.createdBy.initials} ${note.createdBy.lastName}`}
                      title=""
                      date={formatDate(note.createdAt)}
                      attachments={note.attachments}
                    >
                      {note.content}
                    </NoteCard>
                  );
                })
                .otherwise(() => null),
            )}
          </div>
        </div>
        <div className="w-full bottom-0 pb-32 sticky col-span-full bg-gradient-to-t from-grayscale0 via-grayscale0">
          <RichTextEditor className="w-full" onSubmit={handleSendMessage} />
        </div>
      </div>

      <div className="area-page-sidebar">
        <Suspense fallback={null}>
          <TaskDetailsSidebar
            task={tasks}
            brokers={brokers}
            teams={teams}
            onSubmit={handleOptionsUpdate}
          />
        </Suspense>
      </div>
    </div>
  );
}
